import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BarChart3, Clock, LogOut, MapPin, MessageSquare, RefreshCw, ShieldCheck, Trash2, Users } from "lucide-react";
import { Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";
import { adminApi } from "@/lib/api";

const LICENSE_OPTIONS = [
  { key: "1m", label: "+1 Month" },
  { key: "3m", label: "+3 Months" },
  { key: "6m", label: "+6 Months" },
  { key: "1y", label: "+1 Year" },
];

const TABS = [
  { key: "analytics", label: "Analytics", icon: BarChart3 },
  { key: "usage", label: "Usage", icon: Clock },
  { key: "students", label: "Students", icon: Users },
  { key: "messages", label: "Messages", icon: MessageSquare },
];

const PIE_COLORS = ["#4F4CB9", "#F7933E", "#26C4D5", "#84D43E", "#E52A32", "#8B5CF6", "#06B6D4", "#F472B6", "#FBBF24", "#34D399"];

const formatDuration = (seconds) => {
  const s = Math.round(seconds || 0);
  if (s < 60) return `${s}s`;
  const mins = Math.floor(s / 60);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ${mins % 60}m`;
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("analytics");
  const [analytics, setAnalytics] = useState(null);
  const [usage, setUsage] = useState(null);
  const [usageRange, setUsageRange] = useState("all");
  const [students, setStudents] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadUsage = async (range) => {
    try {
      const { data } = await adminApi.get(`/admin/analytics/usage?range=${range}`);
      setUsage(data);
    } catch {
      toast.error("Couldn't load usage analytics.");
    }
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [a, s, m] = await Promise.all([
        adminApi.get("/admin/analytics"),
        adminApi.get("/admin/students"),
        adminApi.get("/admin/messages"),
      ]);
      setAnalytics(a.data);
      setStudents(s.data);
      setMessages(m.data);
      await loadUsage(usageRange);
    } catch {
      toast.error("Session expired. Please log in again.");
      navigate("/admin/login");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRangeChange = (range) => {
    setUsageRange(range);
    loadUsage(range);
  };

  const handleLogout = async () => {
    await adminApi.post("/admin/logout").catch(() => {});
    navigate("/admin/login");
  };

  const handleFlush = async () => {
    if (!window.confirm("Flush all analytics event data? This only clears the aggregate stats view, not student progress.")) return;
    try {
      await adminApi.post("/admin/analytics/flush");
      toast.success("Analytics flushed.");
      loadAll();
    } catch {
      toast.error("Couldn't flush analytics.");
    }
  };

  const handleDeleteStudent = async (id, name) => {
    if (!window.confirm(`Permanently delete student "${name}" and all their data?`)) return;
    try {
      await adminApi.delete(`/admin/students/${id}`);
      toast.success("Student deleted.");
      setStudents((prev) => prev.filter((s) => s.id !== id));
    } catch {
      toast.error("Couldn't delete student.");
    }
  };

  const handleDeleteMessage = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await adminApi.delete(`/admin/messages/${id}`);
      toast.success("Message deleted.");
      setMessages((prev) => prev.filter((m) => m.id !== id));
    } catch {
      toast.error("Couldn't delete message.");
    }
  };

  const handleGrantLicense = async (id, name, duration, label) => {
    if (!window.confirm(`Grant "${name}" ${label.replace("+", "")} of access?`)) return;
    try {
      const { data } = await adminApi.post(`/admin/students/${id}/grant-license`, { duration });
      toast.success(`Granted ${label.replace("+", "")} to ${name}.`);
      setStudents((prev) => prev.map((s) => (s.id === id ? { ...s, license_expires_at: data.license_expires_at, licensed: true } : s)));
    } catch {
      toast.error("Couldn't grant license.");
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground" data-testid="admin-dashboard-loading">Loading admin dashboard...</div>;
  }

  return (
    <div className="min-h-screen bg-[#0B0F19] text-white" data-testid="admin-dashboard-page">
      <div className="border-b border-white/10 px-6 h-16 flex items-center justify-between">
        <p className="font-heading font-extrabold text-lg">SmartGuru.io Admin</p>
        <button onClick={handleLogout} data-testid="admin-logout-button" className="flex items-center gap-2 text-sm text-white/70 hover:text-white">
          <LogOut size={15} /> Log out
        </button>
      </div>
      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex gap-2 mb-6" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`admin-tab-${t.key}`}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-colors ${tab === t.key ? "bg-primary text-primary-foreground" : "bg-white/5 text-white/70 hover:bg-white/10"}`}
            >
              <t.icon size={15} /> {t.label}
            </button>
          ))}
        </div>

        {tab === "analytics" && analytics && (
          <div data-testid="admin-analytics-panel">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-white/50">Live totals: {analytics.live_totals.students} students · {analytics.live_totals.contact_messages} messages</p>
              <button onClick={handleFlush} data-testid="admin-flush-analytics-button" className="flex items-center gap-2 text-sm font-bold bg-secondary text-secondary-foreground px-4 py-2 rounded-xl hover:opacity-90">
                <RefreshCw size={14} /> Flush Analytics
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {Object.entries(analytics.events).length === 0 && (
                <p className="text-white/40 text-sm col-span-3" data-testid="admin-analytics-empty">No events logged yet.</p>
              )}
              {Object.entries(analytics.events).map(([type, count]) => (
                <div key={type} className="bg-white/5 rounded-2xl p-4" data-testid={`admin-event-${type}`}>
                  <p className="text-2xl font-heading font-extrabold">{count}</p>
                  <p className="text-xs text-white/50 mt-1">{type.replaceAll("_", " ")}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "usage" && usage && (
          <div data-testid="admin-usage-panel">
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-white/50">Time-on-platform &amp; location analytics</p>
              <div className="flex gap-1.5 bg-white/5 rounded-xl p-1" data-testid="admin-usage-range-toggle">
                {[{ key: "all", label: "All Time" }, { key: "7d", label: "Last 7 Days" }].map((r) => (
                  <button
                    key={r.key}
                    onClick={() => handleRangeChange(r.key)}
                    data-testid={`admin-usage-range-${r.key}`}
                    className={`text-xs font-bold px-3 py-1.5 rounded-lg transition-colors ${usageRange === r.key ? "bg-primary text-primary-foreground" : "text-white/60 hover:bg-white/10"}`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              <div className="bg-white/5 rounded-2xl p-4" data-testid="admin-usage-session-count">
                <p className="text-2xl font-heading font-extrabold">{usage.session_stats.session_count}</p>
                <p className="text-xs text-white/50 mt-1">Total sessions</p>
              </div>
              <div className="bg-white/5 rounded-2xl p-4" data-testid="admin-usage-avg-duration">
                <p className="text-2xl font-heading font-extrabold">{formatDuration(usage.session_stats.avg_seconds)}</p>
                <p className="text-xs text-white/50 mt-1">Avg session duration</p>
              </div>
              <div className="bg-white/5 rounded-2xl p-4" data-testid="admin-usage-total-duration">
                <p className="text-2xl font-heading font-extrabold">{formatDuration(usage.session_stats.total_seconds)}</p>
                <p className="text-xs text-white/50 mt-1">Total cumulative time</p>
              </div>
            </div>

            <p className="text-sm font-bold text-white/70 mb-3">Module Usage</p>
            <div className="bg-white/5 rounded-2xl p-4 mb-3" data-testid="admin-usage-module-chart">
              {usage.module_time.every((m) => m.seconds === 0) ? (
                <p className="text-white/40 text-sm py-8 text-center" data-testid="admin-usage-module-empty">No module activity logged yet.</p>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={usage.module_time}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                    <XAxis dataKey="module" stroke="rgba(255,255,255,0.5)" fontSize={11} />
                    <YAxis stroke="rgba(255,255,255,0.5)" fontSize={11} tickFormatter={(v) => formatDuration(v)} />
                    <Tooltip formatter={(v) => formatDuration(v)} contentStyle={{ background: "#0B0F19", border: "1px solid rgba(255,255,255,0.1)" }} />
                    <Bar dataKey="seconds" fill="#4F4CB9" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-8">
              {usage.module_time.map((m) => (
                <div key={m.module} className="bg-white/5 rounded-xl p-3" data-testid={`admin-usage-module-${m.module.replaceAll(" ", "-")}`}>
                  <p className="text-sm font-bold">{formatDuration(m.seconds)}</p>
                  <p className="text-xs text-white/50">{m.module}</p>
                </div>
              ))}
            </div>

            <p className="text-sm font-bold text-white/70 mb-3">Student Locations</p>
            {usage.locations.length === 0 ? (
              <p className="text-white/40 text-sm" data-testid="admin-usage-locations-empty">No location data yet.</p>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="bg-white/5 rounded-2xl p-4" data-testid="admin-usage-locations-chart">
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={usage.locations} dataKey="count" nameKey="city" cx="50%" cy="50%" outerRadius={80} label={({ city }) => city}>
                        {usage.locations.map((_, i) => (
                          <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ background: "#0B0F19", border: "1px solid rgba(255,255,255,0.1)" }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-1.5" data-testid="admin-usage-locations-list">
                  {usage.locations.map((l, i) => (
                    <div key={i} className="bg-white/5 rounded-xl p-3 flex items-center justify-between" data-testid={`admin-location-row-${i}`}>
                      <p className="text-sm flex items-center gap-1.5"><MapPin size={13} className="text-secondary" /> {l.city}, {l.country}</p>
                      <p className="text-sm font-bold">{l.count}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}


        {tab === "students" && (
          <div className="space-y-2" data-testid="admin-students-panel">
            {students.length === 0 && <p className="text-white/40 text-sm" data-testid="admin-students-empty">No students yet.</p>}
            {students.map((s) => (
              <div key={s.id} className="bg-white/5 rounded-xl p-4" data-testid={`admin-student-row-${s.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-bold text-sm">{s.name}</p>
                    <p className="text-xs text-white/50">
                      Class {s.standard} · {s.board} · {s.xp} XP ·{" "}
                      {s.licensed ? (
                        <span className="text-success font-semibold" data-testid={`admin-student-license-${s.id}`}>
                          <ShieldCheck size={11} className="inline mr-0.5 -mt-0.5" />
                          Licensed until {new Date(s.license_expires_at).toLocaleDateString()}
                        </span>
                      ) : s.trial_active ? (
                        `Trial: ${s.trial_days_left}d left`
                      ) : (
                        <span className="text-destructive">Trial expired</span>
                      )}
                    </p>
                    <p className="text-xs text-white/40 mt-1" data-testid={`admin-student-usage-${s.id}`}>
                      <Clock size={10} className="inline mr-0.5 -mt-0.5" />
                      {s.usage.session_count} session{s.usage.session_count === 1 ? "" : "s"} · avg {formatDuration(s.usage.avg_seconds)}/session · total {formatDuration(s.usage.total_seconds)}
                    </p>
                  </div>
                  <button onClick={() => handleDeleteStudent(s.id, s.name)} data-testid={`admin-delete-student-${s.id}`} className="text-destructive hover:opacity-80 shrink-0">
                    <Trash2 size={16} />
                  </button>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-3" data-testid={`admin-license-actions-${s.id}`}>
                  {LICENSE_OPTIONS.map((opt) => (
                    <button
                      key={opt.key}
                      onClick={() => handleGrantLicense(s.id, s.name, opt.key, opt.label)}
                      data-testid={`admin-grant-license-${opt.key}-${s.id}`}
                      className="text-[11px] font-bold bg-white/10 hover:bg-primary hover:text-primary-foreground text-white/70 px-2.5 py-1 rounded-lg transition-colors"
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "messages" && (
          <div className="space-y-2" data-testid="admin-messages-panel">
            {messages.length === 0 && <p className="text-white/40 text-sm" data-testid="admin-messages-empty">No messages yet.</p>}
            {messages.map((m) => (
              <div key={m.id} className="bg-white/5 rounded-xl p-4 flex items-start justify-between gap-3" data-testid={`admin-message-row-${m.id}`}>
                <div>
                  <p className="font-bold text-sm">{m.name} <span className="text-white/40 font-normal">· {m.email}</span></p>
                  <p className="text-sm text-white/70 mt-1">{m.message}</p>
                </div>
                <button onClick={() => handleDeleteMessage(m.id)} data-testid={`admin-delete-message-${m.id}`} className="text-destructive hover:opacity-80 shrink-0">
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
